import { FC, useCallback, useMemo, useRef, useState } from 'react';
import stashArrowsSwitch from 'src/redesign/assets/stashArrowsSwitch.svg';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import type { Site } from 'src/types/types';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TextButton } from 'wix-style-react';
import styles from './Dropdown.module.scss';
import { ChevronDown } from 'wix-ui-icons-common';
import c from 'clsx';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { updateSelectedSite } from 'src/redesign/features/Account/store/actions';
import { allUserSitesSelector } from 'src/redesign/features/Sites/selectors';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { DeleteConfirmation } from 'src/redesign/pages/AttendeesPage/components/DeleteConfirmation';
import { API_INSTANCE } from 'src/features/utils';

type TSiteDropdownProps = {
  onAddSiteClick: () => void;
};

// Helper function to get badge color based on site type
const getBadgeStyle = (type: string) => {
  const typeMap = {
    wix: { backgroundColor: '#0C6EFC', color: 'white' },
    shopify: { backgroundColor: '#96bf48', color: 'white' },
    ecwid: { backgroundColor: '#ff6600', color: 'white' },
  };
  return typeMap[type.toLowerCase()] || null;
};

// Helper function to check if site type should show badge
const shouldShowBadge = (type: string) => {
  return ['wix', 'shopify', 'ecwid'].includes(type?.toLowerCase());
};

export const SitesDropdown: FC<TSiteDropdownProps> = ({ onAddSiteClick }) => {
  const dispatch = useAppDispatch();
  const siteButtonRef = useRef<OverlayPanel>();
  const currentSite = useAppSelector(getSelectedSiteDataSelector);
  const allSites = useAppSelector(allUserSitesSelector);
  const [searchQuery, setSearchQuery] = useState('');

  const handlePopupMenuOnClick = useCallback(
    (site: Site) => {
      dispatch(updateSelectedSite({ siteId: site.id }))
        .unwrap()
        .then(() => {
          // Redirect to the root URL instead of reloading the current page
          window.location.reload();
        })
        .catch((err) => {
          console.error('Failed to update selected site', err);
        });

      siteButtonRef.current?.hide();
    },
    [dispatch],
  );

  const handleDeleteSite = useCallback(async (siteId: string) => {
    try {
      await API_INSTANCE.delete(`/api/v1/dashboard/sites/${siteId}`);
      // Reload to refresh the sites list
      window.location.reload();
    } catch (error) {
      console.error('Failed to delete site:', error);
    }
  }, []);
  const itemTemplate = (item: MenuItem) => {
    const site = item.data as Site;
    const badgeStyle = shouldShowBadge(site.type) ? getBadgeStyle(site.type) : null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '4px 8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
          }}
          onClick={() => handlePopupMenuOnClick(site)}
        >
          <span
            className={site.site_display_name?.length > 15 ? 'site-name-tooltip' : ''}
            {...(site.site_display_name?.length > 15 && {
              'data-pr-tooltip': site.site_display_name,
              'data-pr-position': 'top',
            })}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: badgeStyle ? '0 1 auto' : '1',
              maxWidth: badgeStyle ? '140px' : 'none',
            }}
          >
            {site.site_display_name}
          </span>
          {badgeStyle && (
            <Badge
              value={site.type.toUpperCase()}
              style={{
                ...badgeStyle,
                fontSize: '10px',
                borderRadius: '4px',
                fontWeight: '600',
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            />
          )}
        </div>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            marginLeft: 'auto',
            paddingLeft: '16px',
            flexShrink: 0,
          }}
        >
          <DeleteConfirmation
            buttonText={
              <i
                className="pi pi-trash"
                style={{
                  fontSize: '16px',
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              />
            }
            confirmText="Delete this site?"
            successClick={() => handleDeleteSite(site.id)}
            placement="left"
            tooltipContent="Delete site"
          />
        </div>
      </div>
    );
  };

  const sitesModel: MenuItem[] = useMemo(() => {
    const filteredSites = allSites?.filter((site: Site) => {
      const query = searchQuery.toLowerCase();
      const siteName = site.site_display_name?.toLowerCase() || '';
      const siteType = site.type?.toLowerCase() || '';
      return siteName.includes(query) || siteType.includes(query);
    });

    return filteredSites?.map((site: Site): MenuItem => {
      return {
        id: site.id,
        label: site.site_display_name,
        data: site,
        template: itemTemplate,
      };
    });
  }, [handlePopupMenuOnClick, allSites, handleDeleteSite, searchQuery]);
  if (!allSites || !Array.isArray(allSites)) return null;

  return (
    <>
      <Tooltip target=".site-name-tooltip" />
      <TextButton onClick={(e) => siteButtonRef?.current?.toggle(e)}>
        <div className={styles.buttonContent}>
          <img className={styles.arrow} src={stashArrowsSwitch} alt="arrows" />
          <span className={styles.siteName}>
            {currentSite?.site_display_name || 'Site loading...'}
          </span>
          <ChevronDown color="black" className={c(styles.chevron)} />
        </div>
      </TextButton>
      <OverlayPanel
        ref={siteButtonRef}
        style={{ minWidth: '320px' }}
        onHide={() => setSearchQuery('')}
      >
        <span className={styles.menuHeader}>SWITCH TO:</span>
        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
          <span className="p-input-icon-left" style={{ width: '100%' }}>
            <i className="pi pi-search ml-1" />
            <InputText
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-4"
              placeholder="Search sites..."
              autoFocus
            />
          </span>
        </div>
        <Menu
          pt={{
            root: {
              className: styles.rootMenu,
            },
            menu: {
              className: styles.menu,
            },
            menuitem: {
              className: styles.menuItem,
            },
            action: {
              className: styles.menuAction,
            },
          }}
          model={sitesModel}
        />
        <Divider pt={{ root: { className: styles.divider } }} />
        <TextButton onClick={onAddSiteClick} className={styles.addSiteButton}>
          + Add New Site
        </TextButton>
      </OverlayPanel>
    </>
  );
};
